import {
    AccountingReportCycleOptionType,
    AccountingReportCycleTotal,
} from "@common/constants";
import { DEFAULT_VIEW_ID } from "@components/views/constants";

export const TAX_ACCOUNTING_REPORT_VIEW_ID = "tax";

export type AccountingReportBuiltInViewId =
    | typeof DEFAULT_VIEW_ID
    | typeof TAX_ACCOUNTING_REPORT_VIEW_ID;

interface AccountingReportBuiltInViewConfig {
    name: string;
    datePreset: string;
    cycleFor: (
        defaultCycle: AccountingReportCycleOptionType | undefined,
    ) => AccountingReportCycleOptionType | undefined;
}

export interface AccountingReportBuiltInViewSettings {
    name: string;
    datePreset: string;
    cycle?: AccountingReportCycleOptionType;
}

const BUILT_IN_VIEWS: Record<
    AccountingReportBuiltInViewId,
    AccountingReportBuiltInViewConfig
> = {
    [DEFAULT_VIEW_ID]: {
        name: "Default",
        datePreset: "this_year_to_date",
        cycleFor: (defaultCycle) => defaultCycle,
    },
    [TAX_ACCOUNTING_REPORT_VIEW_ID]: {
        name: "Tax view",
        datePreset: "last_year",
        cycleFor: (defaultCycle) =>
            defaultCycle === undefined
                ? undefined
                : AccountingReportCycleTotal.TOTAL,
    },
};

export function isAccountingReportBuiltInView(
    viewId?: string | null,
): viewId is AccountingReportBuiltInViewId {
    return Object.keys(BUILT_IN_VIEWS).some((id) => id === viewId);
}

export function getAccountingReportBuiltInViewIds({
    hasTaxView,
}: {
    hasTaxView: boolean;
}): AccountingReportBuiltInViewId[] {
    return Object.keys(BUILT_IN_VIEWS).filter(
        (id): id is AccountingReportBuiltInViewId =>
            isAccountingReportBuiltInView(id) &&
            (hasTaxView || id !== TAX_ACCOUNTING_REPORT_VIEW_ID),
    );
}

export function getAccountingReportBuiltInViewSettings({
    viewId,
    defaultCycle,
}: {
    viewId: AccountingReportBuiltInViewId;
    defaultCycle: AccountingReportCycleOptionType | undefined;
}): AccountingReportBuiltInViewSettings {
    const { name, datePreset, cycleFor } = BUILT_IN_VIEWS[viewId];
    return { name, datePreset, cycle: cycleFor(defaultCycle) };
}
